import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  FlatTeamSettings,
  UpdateFlatTeamSettings,
} from "@redotech/redo-model/flat-team-settings";
import { toast } from "@redotech/redo-web/alert";
import { NOOP, usePickedTeamSettings } from "../hooks/use-picked-team-settings";
import { TeamContext } from "../team";

/**
 * Hook to manage settings optimization banner logic
 * Handles fetching settings, updating them, and managing completion/dismissal
 */
export function useOptimizationSettings<K extends keyof FlatTeamSettings>({
  applyAllRecommendations: applyAllValues,
  handleComplete,
  handleDismiss,
  shouldCompleteOnAnyChange = false,
}: {
  applyAllRecommendations: NOOP<Pick<UpdateFlatTeamSettings, K>>;
  handleComplete: () => Promise<void>;
  handleDismiss: () => Promise<void>;
  shouldCompleteOnAnyChange?: boolean;
}) {
  const team = useRequiredContext(TeamContext);

  // Derive keys from applyAllRecommendations
  const keys = Object.keys(applyAllValues).reduce(
    (acc, key) => {
      acc[key as K] = 1;
      return acc;
    },
    {} as Record<K, 1>,
  );

  const { query, mutation } = usePickedTeamSettings(team._id, keys);

  const settings = query.data;

  const handleApplyAll = useHandler(async () => {
    await mutation.mutateAsync(applyAllValues);
    await handleComplete();
    toast("Settings applied successfully", { variant: "success" });
  });

  const componentLevelDismiss = useHandler(async () => {
    if (shouldCompleteOnAnyChange && settings) {
      // Check if any setting has been changed from its initial value
      const hasChanges = Object.keys(applyAllValues).some((key) => {
        const keyTyped = key as K;
        return settings[keyTyped] !== query.data?.[keyTyped];
      });

      if (hasChanges) {
        return await handleComplete();
      }
    }
    return await handleDismiss();
  });

  return {
    settings,
    mutation,
    handleApplyAll,
    componentLevelDismiss,
    isLoading: query.isLoading,
  };
}
